import { LoadingOutlined } from '@ant-design/icons';
import { useMemo } from 'react';
import { Modal, Select, Form, Input, Button, Divider, DatePicker } from 'antd';
import {
  useCreateServiceRequest,
  useGetServiceRequestTypes,
} from 'src/hooks/serviceRequest';
import { DobInput, PhoneInput } from 'src/components';
import { VALID_PHONE_REGEX } from 'src/utils/misc';
import { useGetPractitionerList } from 'src/hooks/practitioner';
import dayjs from 'dayjs';
import { showToastError } from 'src/utilities/errortoast';

type CreateReferralModalProps = {
  open: boolean;
  onClose: () => void;
};

export const CreateReferralModal = ({
  open,
  onClose,
}: CreateReferralModalProps) => {
  const [form] = Form.useForm();

  const { mutateAsync: createServiceRequest, isPending } =
    useCreateServiceRequest();

  const { data: serviceRequestTypes, isLoading: isLoadingServiceRequestTypes } =
    useGetServiceRequestTypes();

  const { data: practitionerList, isLoading: isLoadingPractitionerList } =
    useGetPractitionerList({
      is_shown: true,
    });

  const practitionerOptions = useMemo(
    () =>
      practitionerList?.practitioners?.map((practitioner) => ({
        label: practitioner.full_name,
        value: practitioner.practitioner_id,
      })) || [],
    [practitionerList]
  );

  const handleSubmit = async () => {
    const phoneCountryCode = '+1';
    try {
      const {
        referralType,
        firstName,
        lastName,
        email,
        phone,
        dateOfBirth,
        startTime,
        performingPractitioner,
        requestingPractitioner,
      } = await form.validateFields();
      const payload = {
        patient_first_name: firstName,
        patient_last_name: lastName,
        patient_email: email,
        patient_phone: phoneCountryCode + phone.replace(/\D/g, ''),
        patient_birth_date: dayjs(dateOfBirth).format('YYYY-MM-DD'),
        start_time: startTime.toISOString(),
        performing_practitioner_id: performingPractitioner,
        type_id: referralType,
      };

      if (requestingPractitioner) {
        const practitioner = practitionerList?.practitioners?.find(
          (practitioner) =>
            practitioner.practitioner_id === requestingPractitioner
        );
        Object.assign(payload, {
          requesting_practitioner: {
            practitioner_id: practitioner?.practitioner_id,
            first_name: practitioner?.first_name,
            last_name: practitioner?.last_name,
            title: practitioner?.title,
            full_name: practitioner?.full_name,
          },
        });
      }

      await createServiceRequest(payload);
      onClose?.();
    } catch (error) {
      showToastError('Failed to create referral');
    }
  };

  return (
    <Modal
      width={800}
      open={open}
      onCancel={onClose}
      centered
      title="Create request"
      footer={
        <div className="flex justify-end gap-2 mt-8">
          <Button
            disabled={isPending}
            className="ih-button-secondary"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            loading={isPending}
            className="ih-button-primary"
            onClick={handleSubmit}
          >
            Create
          </Button>
        </div>
      }
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="referralType"
          label="Select Type"
          className="field-wrapper"
          rules={[{ required: true, message: 'Referral type is required' }]}
        >
          <Select
            prefix={isLoadingServiceRequestTypes ? <LoadingOutlined /> : null}
            className="field capitalize"
            classNames={{
              popup: {
                root: 'capitalize',
              },
            }}
            placeholder="Select referral type"
            options={serviceRequestTypes?.map(
              ({ service_request_type, service_request_type_id }) => ({
                label: service_request_type,
                value: service_request_type_id,
              })
            )}
            loading={isLoadingServiceRequestTypes}
            disabled={!serviceRequestTypes?.length}
          />
        </Form.Item>
        <Divider />
        <div className="flex gap-4">
          <Form.Item
            className="field-wrapper flex-1"
            label="First Name"
            name="firstName"
            rules={[
              {
                required: true,
                message: 'First name is required',
              },
            ]}
          >
            <Input placeholder="Enter first name" className="field" />
          </Form.Item>
          <Form.Item
            className="field-wrapper flex-1"
            label="Last Name"
            name="lastName"
            rules={[{ required: true, message: 'Last name is required' }]}
          >
            <Input placeholder="Enter last name" className="field" />
          </Form.Item>
        </div>
        <div className="flex gap-4">
          <Form.Item
            className="field-wrapper flex-1"
            label="Email"
            name="email"
            rules={[
              { required: true, message: 'Email is required' },
              { type: 'email', message: 'Invalid email address' },
            ]}
          >
            <Input placeholder="Enter email" className="field" />
          </Form.Item>
          <Form.Item
            className="field-wrapper flex-1"
            label="Phone"
            name="phone"
            rules={[
              { required: true, message: 'Phone is required' },
              {
                pattern: VALID_PHONE_REGEX,
                message: 'Invalid phone number',
              },
            ]}
          >
            <PhoneInput className="field" />
          </Form.Item>
        </div>
        <div className="flex gap-4 justify-stretch">
          <Form.Item
            className="field-wrapper flex-1"
            label="Date of Birth"
            name="dateOfBirth"
            rules={[{ required: true, message: 'Date of birth is required' }]}
          >
            <DobInput className="field" />
          </Form.Item>
          <Form.Item
            className="field-wrapper flex-1"
            label="Start Time"
            name="startTime"
            rules={[{ required: true, message: 'Start time is required' }]}
          >
            <DatePicker
              format="MM/DD/YYYY HH:mm"
              className="field"
              placeholder="Select start time"
              showTime={{
                defaultOpenValue: dayjs('09:00', 'HH:mm'),
              }}
            />
          </Form.Item>
        </div>
        <Divider className="mt-4" />
        <div className="md:flex gap-4">
          <Form.Item
            className="field-wrapper flex-1"
            label="Performing Practitioner"
            name="performingPractitioner"
            rules={[
              {
                required: true,
                message: 'Performing practitioner is required',
              },
            ]}
          >
            <Select
              className="field"
              showSearch
              filterOption={(input, option) => {
                return !!option?.label
                  ?.toLowerCase()
                  .includes(input?.toLowerCase());
              }}
              placeholder="Select performing practitioner"
              options={practitionerOptions}
              loading={isLoadingPractitionerList}
            />
          </Form.Item>
          <Form.Item
            className="field-wrapper flex-1"
            label="Requesting Practitioner"
            name="requestingPractitioner"
          >
            <Select
              className="field"
              showSearch
              filterOption={(input, option) => {
                return !!option?.label
                  ?.toLowerCase()
                  .includes(input?.toLowerCase());
              }}
              placeholder="Select requesting practitioner"
              options={practitionerOptions}
              loading={isLoadingPractitionerList}
            />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
};
